import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AddGroupMembersResponse,
  GroupMemberAddedEnvelope,
  GroupMemberQuery,
  GroupMemberRemovedEnvelope,
  ListGroupMembersOptions,
  ListGroupMembersResponse,
  ListMembershipsOptions,
  ListMembershipsResponse,
  MembersQueryBuilder,
  QueryGroupMembersResponse,
  QueryMembershipsOptions,
  QueryMembershipsResponse,
  addGroupMembers as universalAddGroupMembers,
  listGroupMembers as universalListGroupMembers,
  listMemberships as universalListMemberships,
  queryGroupMembers as universalQueryGroupMembers,
  queryMemberships as universalQueryMemberships,
  removeGroupMembers as universalRemoveGroupMembers,
  typedQueryGroupMembers as universalTypedQueryGroupMembers,
} from './social-groups-v2-group-member-members.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/groups' };

export function addGroupMembers(
  httpClient: HttpClient
): AddGroupMembersSignature {
  return (groupId: string, memberIds: string[]) =>
    universalAddGroupMembers(
      groupId,
      memberIds,
      // @ts-ignore
      { httpClient }
    );
}

interface AddGroupMembersSignature {
  /**
   * Add site members to a specific group. Public members are added right away, while private members receive an invitation to join the group.
   *
   * For `SECRET` groups, group admins and group members can add additional members to their group.
   * For `PUBLIC` and `PRIVATE` groups, only group admins can add additional members to their group.
   * They can also allow all group members to add a new member to the group with `group.settings`.
   * @param - Group ID.
   * @param - Member IDs. See the Members API for details.
   */
  (groupId: string, memberIds: string[]): Promise<
    NonNullablePaths<
      AddGroupMembersResponse,
      `members` | `members.${number}.memberId` | `members.${number}.role.value`,
      5
    >
  >;
}

export function removeGroupMembers(
  httpClient: HttpClient
): RemoveGroupMembersSignature {
  return (groupId: string, memberIds: string[]) =>
    universalRemoveGroupMembers(
      groupId,
      memberIds,
      // @ts-ignore
      { httpClient }
    );
}

interface RemoveGroupMembersSignature {
  /**
   * Removes site members from a specific group.
   *
   * Only group admins can remove members from their group.
   * @param - Group ID.
   * @param - Member IDs. See the Members API for details.
   */
  (groupId: string, memberIds: string[]): Promise<void>;
}

export function listGroupMembers(
  httpClient: HttpClient
): ListGroupMembersSignature {
  return (groupId: string, options?: ListGroupMembersOptions) =>
    universalListGroupMembers(
      groupId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListGroupMembersSignature {
  /**
   * Retrieves a list of up to 100 group members, given the provided paging.
   *
   * For groups with `group.privacyStatus` set to `SECRET`, only group admins and group members can see the group and its content.
   *
   * Default sorts by `joinedDate` in descending order.
   * @param - Group ID.
   * @param - Paging options.
   */
  (groupId: string, options?: ListGroupMembersOptions): Promise<
    NonNullablePaths<
      ListGroupMembersResponse,
      `members` | `members.${number}.memberId` | `members.${number}.role.value`,
      5
    >
  >;
}

export function queryGroupMembers(
  httpClient: HttpClient
): QueryGroupMembersSignature {
  return (groupId: string) =>
    universalQueryGroupMembers(
      groupId,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryGroupMembersSignature {
  /**
   * Retrieves a list of up to 100 group members, given the provided paging, sorting and filtering.
   *
   *
   * Creates a query to retrieve a list of group members.
   *
   * > **Note:** For `SECRET` groups, only group admins and group members can query group members. <!-- However, if the `suppressAuth` option is set to `true`, all permissions are overwritten, and all site members (including non-group members) can query group members.  -->
   *
   * The `queryGroupMembers()` function builds a query to retrieve a list of all group members, and returns a [GroupMembersQueryBuilder](#membersquerybuilder) object.
   *
   * The returned object contains the query definition which is typically used to run the query using the [`find()`](#membersquerybuilder/find) function.
   *
   * You can refine the query by chaining `GroupMembersQueryBuilder` functions onto the query. `GroupMembersQueryBuilder` functions enable you to sort, filter, and control the results that `queryMembers()` returns.
   *
   *  `queryGroupMembers()` runs with these `GroupMembersQueryBuilder` defaults, which you can override:
   *  + [`limit(100)`](/members-query-builder/limit)
   *  + [`descending("joinedDate")`](/members-query-builder/descending)
   *
   * The following `GroupMembersQueryBuilder` functions are supported for `queryGroupMembers()`. For a full description of the Members object, see the object returned for the [`items`](/members-query-result/items) property in `MembersQueryResult`.
   * @param - Group ID.
   */
  (groupId: string): MembersQueryBuilder;
}

export function typedQueryGroupMembers(
  httpClient: HttpClient
): TypedQueryGroupMembersSignature {
  return (query: GroupMemberQuery) =>
    universalTypedQueryGroupMembers(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryGroupMembersSignature {
  /** */
  (query: GroupMemberQuery): Promise<
    NonNullablePaths<
      QueryGroupMembersResponse,
      `members` | `members.${number}.memberId` | `members.${number}.role.value`,
      5
    >
  >;
}

export function listMemberships(
  httpClient: HttpClient
): ListMembershipsSignature {
  return (memberId: string, options?: ListMembershipsOptions) =>
    universalListMemberships(
      memberId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListMembershipsSignature {
  /**
   * Retrieves a list of group memberships of a site member.
   *
   * Only Wix users can retrieve a site member's group memberships.
   * Site members can only retrieve a list of their own group memberships.
   *
   * Default sorts by `groupId` in descending order.
   * @param - Site member ID.
   * @param - Paging options.
   */
  (memberId: string, options?: ListMembershipsOptions): Promise<
    NonNullablePaths<
      ListMembershipsResponse,
      | `memberships`
      | `memberships.${number}.groupId`
      | `memberships.${number}.status`
      | `memberships.${number}.role.value`,
      5
    >
  >;
}

export function queryMemberships(
  httpClient: HttpClient
): QueryMembershipsSignature {
  return (memberId: string, options?: QueryMembershipsOptions) =>
    universalQueryMemberships(
      memberId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryMembershipsSignature {
  /**
   * Retrieves a list of up to 100 members and their membership status, given the provided paging and filtering.
   *
   * The queryMemberships function returns a Promise that resolves to a list of memberships.
   *
   * >**Note:** Site members can only query their own memberships.
   *
   * | Property                    | Supported Filters & Sorting                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                            |
   * | --------------------------- | -------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- |
   * | `status`                    | [`eq()`](#membershipsquerybuilder/eq), [`ne()`](#membershipsquerybuilder/ne)             |
   * | `role`                      | [`eq()`](#membershipsquerybuilder/eq), [`ne()`](#membershipsquerybuilder/ne)             |
   *
   * @param - Site member ID.
   */
  (memberId: string, options?: QueryMembershipsOptions): Promise<
    NonNullablePaths<
      QueryMembershipsResponse,
      | `memberships`
      | `memberships.${number}.groupId`
      | `memberships.${number}.status`
      | `memberships.${number}.role.value`,
      5
    >
  >;
}

export const onGroupMemberAdded = EventDefinition(
  'wix.social_groups.v2.group_member_added',
  true,
  (event: GroupMemberAddedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.groupMember.joinedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<GroupMemberAddedEnvelope>();
export const onGroupMemberRemoved = EventDefinition(
  'wix.social_groups.v2.group_member_removed',
  true,
  (event: GroupMemberRemovedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.groupMember.joinedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<GroupMemberRemovedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AddGroupMembersRequest,
  AddGroupMembersResponse,
  BaseEventMetadata,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  GroupMember,
  GroupMemberAddedEnvelope,
  GroupMemberQuerySpec,
  GroupMemberRemovedEnvelope,
  GroupRole,
  IdentificationData,
  IdentificationDataIdOneOf,
  JoinBy,
  JoinRequest,
  JoinRequestsApproved,
  JoinResponse,
  LeaveRequest,
  LeaveResponse,
  ListGroupMembersOptions,
  ListGroupMembersRequest,
  ListGroupMembersResponse,
  ListMembershipsOptions,
  ListMembershipsRequest,
  ListMembershipsResponse,
  MemberAdded,
  MemberJoined,
  MemberJoinedGroup,
  MemberLeft,
  MemberRemoved,
  MembersAddedToGroup,
  MembersInvitedToGroup,
  MembersQueryBuilder,
  MembersQueryResult,
  Membership,
  MembershipQuestionAnswer,
  MembershipStatus,
  MessageEnvelope,
  Paging,
  PagingMetadata,
  Query,
  QueryGroupMembersRequest,
  QueryGroupMembersResponse,
  QueryMembershipsOptions,
  QueryMembershipsRequest,
  QueryMembershipsResponse,
  QueryMyMembershipRequest,
  QueryMyMembershipResponse,
  QueryNonGroupMembersRequest,
  QueryNonGroupMembersResponse,
  RemoveGroupMembersRequest,
  RemoveGroupMembersResponse,
  RestoreInfo,
  Role,
  SocialGroupsEvent,
  SocialGroupsEventPayloadOneOf,
  SortOrder,
  Sorting,
  WebhookIdentityType,
  utils,
} from './social-groups-v2-group-member-members.universal.js';
